import { type FC } from 'react';
import classnames from 'classnames';
import { defineMessages, useIntl } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { Link } from 'dibs-elements/exports/Link';
import { nftFaqUrl } from 'dibs-crypto-utils/exports/constants';

import { DiscordIcon } from './DiscordIcon';
import { FormattedExpandingList } from './FormattedExpandingList';
import { FormattedExpandingSection } from './FormattedExpandingSection';

import styles from './NftFaq.scss';

const messages = defineMessages({
    heading: { id: 'dibs-nft-faq.heading', defaultMessage: 'NFT Sales on 1stDibs' },
    browse: { id: 'dibs-nft-faq.browse', defaultMessage: 'Browse Collection' },
    discord: { id: 'dibs-nft-faq.discord', defaultMessage: 'Join the 1stDibs server' },
    whatHeading: { id: 'dibs-nft-faq.whatHeading', defaultMessage: `What is an NFT?` },
    whatSection: {
        id: 'dibs-nft-faq.what',
        defaultMessage: `Non-Fungible Tokens (NFTs) are unique digital items with blockchain-managed metadata regarding their provenance and ownership.`,
    },
    whatAgainHeading: {
        id: 'dibs-nft-faq.whatAgainHeading',
        defaultMessage: `What am I buying when I buy an NFT?`,
    },
    whatAgainSection: {
        id: 'dibs-nft-faq.whatAgain',
        defaultMessage: `Buyers are purchasing a digital item and a unique token on the Ethereum blockchain, which proves the authenticity and provenance of the item.`,
    },
    howHeading: { id: 'dibs-nft-faq.howHeading', defaultMessage: `How does an auction work?` },
    howSection: {
        id: 'dibs-nft-faq.how',
        defaultMessage: `Creators set a reserve price for an item. Once the first bid is placed at the reserve price or higher, an auction begins and typically lasts 24 hours.`,
    },
    aboutHeading: { id: 'dibs-nft-faq.aboutHeading', defaultMessage: `About NFTs on 1stDibs` },
    aboutSection: {
        id: 'dibs-nft-faq.about',
        defaultMessage: `NFTs are an innovative form of technology that engages creators of all mediums. We’re thrilled to offer our community of collectors and creators an opportunity to support self-expression and make meaningful connections in the digital world. <a>Learn More</a>`,
    },
    stepsHeading: { id: 'dibs-nft-faq.steps', defaultMessage: 'How To Bid on an Item' },
    stepsList: {
        id: 'dibs-nft-faq.howList',
        defaultMessage: `
    <li>Create a 1stDibs account or sign in to your existing account.</li>
    <li><span>Connect your digital wallet through your <settings>account settings</settings> or before you place your bid.</span></li>
    <li>Place your bid.</li>
    <li>We'll email you if you've been outbid or if you're the winner.</li>
    <li>Claim your NFT by visiting the item page.</li>
    `,
    },
});

export const NftFaq: FC<{ hasBrowseLink?: boolean; deviceType?: string }> = ({
    hasBrowseLink,
    deviceType,
}) => {
    const { formatMessage } = useIntl();
    return (
        <div className={styles.fullWidthContainer}>
            <HeadingLevel>
                {Heading => (
                    <aside className={styles.gridContainer}>
                        <div className={styles.headingGrid}>
                            <Heading className={styles.heading}>
                                {formatMessage(messages.heading)}
                            </Heading>
                            {hasBrowseLink && (
                                <Link className={styles.browseLink} href={'/nft-collections/'}>
                                    {formatMessage(messages.browse)}
                                </Link>
                            )}
                            <DiscordIcon className={classnames(styles.icon, styles.discordAside)} />
                            <Link
                                className={styles.discordAside}
                                href="https://discord.gg/4XhNgZwuBj"
                            >
                                {formatMessage(messages.discord)}
                            </Link>
                        </div>
                        <HeadingLevel>
                            <div className={styles.multiColumn}>
                                <FormattedExpandingSection
                                    deviceType={deviceType}
                                    Heading={Heading}
                                    headingDescriptor={messages.whatHeading}
                                    bodyDescriptor={messages.whatSection}
                                />
                                <FormattedExpandingSection
                                    deviceType={deviceType}
                                    Heading={Heading}
                                    headingDescriptor={messages.whatAgainHeading}
                                    bodyDescriptor={messages.whatAgainSection}
                                />
                                <FormattedExpandingSection
                                    deviceType={deviceType}
                                    Heading={Heading}
                                    headingDescriptor={messages.howHeading}
                                    bodyDescriptor={messages.howSection}
                                />
                                <FormattedExpandingSection
                                    deviceType={deviceType}
                                    Heading={Heading}
                                    headingDescriptor={messages.aboutHeading}
                                    bodyDescriptor={messages.aboutSection}
                                    bodyParams={{
                                        a: parts => (
                                            <Link
                                                href={nftFaqUrl}
                                                dataTn="nft-faq-link"
                                                key="nft-faq-link"
                                                target="_blank"
                                            >
                                                {parts}
                                            </Link>
                                        ),
                                    }}
                                />
                            </div>
                            <div className={styles.listColumn}>
                                <FormattedExpandingList
                                    deviceType={deviceType}
                                    Heading={Heading}
                                    headingDescriptor={messages.stepsHeading}
                                    listDescriptor={messages.stepsList}
                                />
                            </div>
                        </HeadingLevel>
                    </aside>
                )}
            </HeadingLevel>
        </div>
    );
};
